import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import { getTenantSubdomain } from '../../utils/subdomain';

const resolveTenantId = (connection) => {
  if (!connection?.tenantId) return null;
  if (typeof connection.tenantId === 'string') return connection.tenantId;
  return connection.tenantId._id || connection.tenantId.id || null;
};

const normalizeSubdomain = (value) => {
  if (!value) return null;
  return value.toString().trim().toLowerCase();
};

const getConnectionSubdomain = (connection) => {
  if (!connection) return null;
  if (connection.subdomain) {
    return normalizeSubdomain(connection.subdomain);
  }
  if (typeof connection.tenantId === 'object' && connection.tenantId !== null) {
    return normalizeSubdomain(connection.tenantId.subdomain);
  }
  return null;
};

const persistUser = (user) => {
  if (user && typeof localStorage !== 'undefined') {
    localStorage.setItem('user', JSON.stringify(user));
  }
};

const applySubdomainSelection = (user) => {
  const subdomain = getTenantSubdomain();
  if (!subdomain || !user?.tenantConnections?.length) {
    return { user, matched: false };
  }

  const desiredSubdomain = normalizeSubdomain(subdomain);
  const connection = user.tenantConnections.find((tc) => {
    if (!tc?.approved) return false;
    return getConnectionSubdomain(tc) === desiredSubdomain;
  });

  if (!connection) {
    return { user, matched: false };
  }

  const tenantId = resolveTenantId(connection);
  if (!tenantId) {
    return { user, matched: false };
  }

  const updatedUser = { ...user, tenantId };
  persistUser(updatedUser);
  return { user: updatedUser, matched: true };
};

const applySingleConnectionSelection = (user, isCustomerUser) => {
  if (!isCustomerUser || !user?.tenantConnections || user.tenantConnections.length !== 1) {
    return { user, matched: false };
  }

  const [connection] = user.tenantConnections;
  if (!connection?.approved) {
    return { user, matched: false };
  }

  const tenantId = resolveTenantId(connection);
  if (!tenantId) {
    return { user, matched: false };
  }

  const updatedUser = { ...user, tenantId };
  persistUser(updatedUser);
  return { user: updatedUser, matched: true };
};

const processUserState = (user) => {
  if (!user) {
    return { user: null, shouldRedirect: false };
  }

  const isCustomerUser = !user.userType || user.userType === 'customer';

  const subdomainSelection = applySubdomainSelection(user);
  if (subdomainSelection.matched) {
    return {
      user: subdomainSelection.user,
      shouldRedirect: isCustomerUser,
    };
  }

  const singleConnectionSelection = applySingleConnectionSelection(user, isCustomerUser);
  if (singleConnectionSelection.matched) {
    return {
      user: singleConnectionSelection.user,
      shouldRedirect: isCustomerUser,
    };
  }

  return {
    user,
    shouldRedirect: false,
  };
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password, subdomain } = {}, { rejectWithValue }) => {
    try {
      const response = await authService.login(email, password, subdomain);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser();
      return response.user;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(profileData);
      return response.user;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const storedUser = authService.getUser();
const processedUserState = processUserState(storedUser);

const initialState = {
  user: processedUserState.user,
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  error: null,
  shouldRedirectToDashboard: processedUserState.shouldRedirect,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      authService.logout();
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.shouldRedirectToDashboard = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearRedirectFlag: (state) => {
      state.shouldRedirectToDashboard = false;
    },
    setSelectedTenantForCustomer: (state, action) => {
      console.log('🎯 Redux setSelectedTenantForCustomer called:', {
        payload: action.payload,
        currentUserType: state.user?.userType,
        currentTenantId: state.user?.tenantId
      });
      
      // Tretiraj korisnike bez userType kao customer
      const isCustomer = !state.user?.userType || state.user.userType === 'customer';
      
      if (isCustomer) {
        // Kreiraj novi user objekat da trigger-uje re-render
        state.user = { ...state.user, tenantId: action.payload };
        // Ažuriraj localStorage
        localStorage.setItem('user', JSON.stringify(state.user));
        console.log('✅ Redux - Updated user.tenantId to:', action.payload);
      }
    },
    updateUser: (state, action) => {
      // Update user in state
      state.user = { ...state.user, ...action.payload };
      // Also update localStorage
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    setDemoUser: (state, action) => {
      // Postavi demo user-a i označi kao autentifikovanog
      state.user = action.payload;
      state.isAuthenticated = true;
      localStorage.setItem('user', JSON.stringify(action.payload));
      // Ne postavljamo token jer demo mode ne koristi token
      // Ukloni token ako postoji iz prethodne sesije
      localStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;

        const userFromStorage = authService.getUser();
        state.user = userFromStorage || action.payload.user;
        state.error = null;

        const processedState = processUserState(state.user);
        state.user = processedState.user;
        state.shouldRedirectToDashboard = processedState.shouldRedirect;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;

        const processedState = processUserState(state.user);
        state.user = processedState.user;
        state.shouldRedirectToDashboard = processedState.shouldRedirect;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError, clearRedirectFlag, setSelectedTenantForCustomer, updateUser, setDemoUser } = authSlice.actions;
export default authSlice.reducer;
